import React from 'react'
import { useTable } from '@refinedev/antd'
import { useUpdate } from '@refinedev/core'
import { Switch, Tooltip } from 'antd'
import { brand } from '../../theme/brand'
import { ListPage, SectionCard, type Stat } from '../../theme/listkit'
import { ShieldCheck, ShieldAlert, MessagesSquare } from '../../components/icons'

interface FeatureFlag {
  key: string
  enabled: boolean
  description: string | null
  updated_at: string
}

/**
 * Standalone privacy/consent flag — NOT under integrity_master. Surfaced on
 * this page alongside the abuse controls, but kept visually distinct because
 * it gates a member-consent flow (first-time DM requests), not moderation.
 * The mobile app subscribes to feature_flags Realtime, so toggling is live.
 */
const DM_APPROVAL_KEY = 'dm_approval_required'

const DM_APPROVAL_META = {
  title: 'DM approval (message requests)',
  note: 'Require recipients to approve a first-time direct message request before the conversation starts. Off = DMs open instantly.',
}

/**
 * Emergency kill switch for ALL member photo/image uploads. Unlike the integrity
 * flags (which pause moderation), this is ENFORCED SERVER-SIDE: feature_flags
 * `uploads_enabled` is read by a RESTRICTIVE RLS policy on the photos/avatars
 * storage buckets + the photos table, so turning it OFF denies uploads in the
 * database even for a client that bypasses the app UI. Admins/service_role stay
 * exempt. enabled = true means uploads are allowed (normal).
 */
const UPLOADS_KEY = 'uploads_enabled'

const UPLOADS_META = {
  title: 'Member photo uploads',
  note: 'ON = members can upload photos (normal). Turn OFF to immediately block ALL member uploads — gallery, avatar, and feed — enforced in the database (RLS), not just hidden in the app. Admins stay exempt. Use during an inappropriate-image incident.',
}

/**
 * Friendly labels for integrity_* feature flag keys. integrity_master is a
 * gate for ALL Trust & Safety features — flipping it off disables everything
 * downstream even if individual flags are still on.
 */
const FLAG_LABELS: Record<string, { title: string; note: string }> = {
  integrity_master: {
    title: 'Master switch (all Trust & Safety)',
    note: 'Gates every integrity feature below. Disable to pause all moderation.',
  },
  integrity_image_moderation: {
    title: 'Image moderation (AWS Rekognition)',
    note: 'Scans uploaded photos for explicit content via Rekognition. Disable to pause auto-review.',
  },
  integrity_reporting: {
    title: 'Member reporting',
    note: 'Allows members to flag posts, profiles, and messages for review.',
  },
  integrity_blocking: {
    title: 'Member blocking',
    note: 'Allows members to block one another.',
  },
  integrity_phototag_consent: {
    title: 'Photo-tag consent',
    note: 'Requires tagged members to approve before a tag is published.',
  },
  integrity_text_filter: {
    title: 'Text blocklist',
    note: 'Screens new text content against the active blocklist terms.',
  },
}

/** Canonical display order: master first, image moderation (the headline) second. */
const FLAG_ORDER = [
  'integrity_master',
  'integrity_image_moderation',
  'integrity_reporting',
  'integrity_blocking',
  'integrity_phototag_consent',
  'integrity_text_filter',
]

function sortedIntegrityFlags(flags: FeatureFlag[]): FeatureFlag[] {
  return [...flags].sort((a, b) => {
    const ai = FLAG_ORDER.indexOf(a.key)
    const bi = FLAG_ORDER.indexOf(b.key)
    if (ai === -1 && bi === -1) return a.key.localeCompare(b.key)
    if (ai === -1) return 1
    if (bi === -1) return -1
    return ai - bi
  })
}

/**
 * Safety Controls — a focused admin surface for all `integrity_*` feature flags.
 * Changes take effect in real time: the mobile app subscribes to the feature_flags
 * Realtime channel, and Edge Functions re-read the flag per request. No deploy needed.
 */
export function IntegritySettingsPage() {
  // feature_flags is a small, domain-bounded table; an explicit bounded pageSize
  // (200 >> the real key count) keeps the whole flag set on one page for the
  // integrity_* filtering below without ever issuing an unbounded fetch.
  const { tableProps } = useTable<FeatureFlag>({
    resource: 'feature_flags',
    syncWithLocation: false,
    pagination: { pageSize: 200 },
    meta: { idColumnName: 'key' },
  })

  const { mutate: update } = useUpdate()

  const allFlags: FeatureFlag[] = (tableProps.dataSource ?? []) as FeatureFlag[]
  // Show the integrity_* abuse controls AND the standalone dm_approval_required
  // privacy/consent control. dm_approval_required is NOT integrity-prefixed, so
  // include it explicitly. It is rendered in its own labelled row below.
  const integrityFlags = sortedIntegrityFlags(
    allFlags.filter((f) => f.key.startsWith('integrity_')),
  )
  const dmApprovalFlag = allFlags.find((f) => f.key === DM_APPROVAL_KEY) ?? null
  const uploadsFlag = allFlags.find((f) => f.key === UPLOADS_KEY) ?? null

  const surfacedFlags: FeatureFlag[] = dmApprovalFlag
    ? [...integrityFlags, dmApprovalFlag]
    : integrityFlags

  const enabledCount = surfacedFlags.filter((f) => f.enabled).length
  const total = surfacedFlags.length

  const stats: Stat[] = [
    {
      label: 'Controls Enabled',
      value: `${enabledCount} / ${total || 6}`,
      icon: ShieldCheck,
      tone: enabledCount > 0 ? 'green' : 'neutral',
    },
  ]

  function toggleFlag(key: string, enabled: boolean) {
    update({
      resource: 'feature_flags',
      id: key,
      values: { enabled },
      meta: { idColumnName: 'key' },
    })
  }

  const isLoading = tableProps.loading as boolean

  return (
    <ListPage
      intro={{
        eyebrow: 'Trust & Safety · Safety Controls',
        description:
          'Toggle integrity and privacy controls on or off. Changes take effect within seconds — no deploy required. The master switch gates the abuse controls below it; the DM approval consent control is independent.',
      }}
      stats={stats}
      statColumns={1}
    >
      <SectionCard title="Safety Controls">
        {isLoading ? (
          <div
            style={{
              fontFamily: brand.fSans,
              fontWeight: 500,
              fontSize: 13,
              color: brand.sub,
              padding: '24px 0',
            }}
          >
            Loading controls…
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {uploadsFlag && (
              <div data-testid="uploads-killswitch-section" style={{ marginBottom: 22 }}>
                <div
                  style={{
                    fontFamily: brand.fSans,
                    fontWeight: 700,
                    fontSize: 11,
                    letterSpacing: '.08em',
                    textTransform: 'uppercase',
                    color: '#b23a30',
                    marginBottom: 10,
                  }}
                >
                  Emergency
                </div>
                <div
                  data-testid={`integrity-row-${uploadsFlag.key}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 16,
                    background: 'rgba(178,58,48,.06)',
                    borderRadius: 10,
                    padding: '18px 14px',
                    border: '1px solid rgba(178,58,48,.28)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flex: 1 }}>
                    <div style={{ marginTop: 2, flexShrink: 0 }}>
                      <ShieldAlert size={18} color={'#b23a30'} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontFamily: brand.fSans,
                          fontWeight: 700,
                          fontSize: 13,
                          color: brand.ink,
                          marginBottom: 3,
                        }}
                      >
                        {UPLOADS_META.title}
                        <span
                          style={{
                            marginLeft: 8,
                            fontFamily: brand.fSans,
                            fontWeight: 700,
                            fontSize: 10,
                            color: '#b23a30',
                            background: 'rgba(178,58,48,.12)',
                            padding: '2px 7px',
                            borderRadius: 999,
                            verticalAlign: 'middle',
                          }}
                        >
                          KILL SWITCH
                        </span>
                      </div>
                      <div
                        style={{
                          fontFamily: brand.fSans,
                          fontWeight: 500,
                          fontSize: 12,
                          color: brand.sub,
                          lineHeight: 1.5,
                        }}
                      >
                        {UPLOADS_META.note}
                      </div>
                      <div
                        style={{
                          fontFamily: brand.fSans,
                          fontWeight: 500,
                          fontSize: 11,
                          color: brand.sub,
                          marginTop: 4,
                          opacity: 0.65,
                        }}
                      >
                        Last changed {new Date(uploadsFlag.updated_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                  <div style={{ flexShrink: 0 }}>
                    <Tooltip
                      title={
                        uploadsFlag.enabled
                          ? 'Photo uploads are ON — turn OFF to block all member uploads'
                          : 'Photo uploads are BLOCKED — turn ON to allow'
                      }
                    >
                      <Switch
                        checked={uploadsFlag.enabled}
                        onChange={(checked) => toggleFlag(uploadsFlag.key, checked)}
                        style={{ background: uploadsFlag.enabled ? undefined : '#b23a30' }}
                      />
                    </Tooltip>
                  </div>
                </div>
              </div>
            )}
            {integrityFlags.map((flag, idx) => {
              const isMaster = flag.key === 'integrity_master'
              const isImageMod = flag.key === 'integrity_image_moderation'
              const meta = FLAG_LABELS[flag.key] ?? { title: flag.key, note: flag.description ?? '' }

              return (
                <div
                  key={flag.key}
                  data-testid={`integrity-row-${flag.key}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 16,
                    ...(isMaster
                      ? {
                          background: 'rgba(196,151,49,.06)',
                          borderRadius: 10,
                          padding: '18px 14px',
                          marginBottom: 8,
                          border: `1px solid rgba(196,151,49,.22)`,
                        }
                      : isImageMod
                      ? {
                          background: 'rgba(30,78,140,.04)',
                          borderRadius: 8,
                          padding: '16px 12px',
                          marginBottom: 4,
                          border: `1px solid rgba(30,78,140,.14)`,
                        }
                      : {
                          padding: '16px 0',
                          borderBottom:
                            idx < integrityFlags.length - 1
                              ? `1px solid ${brand.cardLine}`
                              : 'none',
                        }),
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flex: 1 }}>
                    <div style={{ marginTop: 2, flexShrink: 0 }}>
                      {isMaster ? (
                        <ShieldAlert size={18} color={brand.gold} />
                      ) : (
                        <ShieldCheck size={18} color={brand.jaguar} />
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontFamily: brand.fSans,
                          fontWeight: isMaster || isImageMod ? 700 : 600,
                          fontSize: isMaster ? 14 : 13,
                          color: brand.ink,
                          marginBottom: 3,
                        }}
                      >
                        {meta.title}
                        {isImageMod && (
                          <span
                            style={{
                              marginLeft: 8,
                              fontFamily: brand.fSans,
                              fontWeight: 700,
                              fontSize: 10,
                              color: brand.jaguar,
                              background: 'rgba(30,78,140,.10)',
                              padding: '2px 7px',
                              borderRadius: 999,
                              verticalAlign: 'middle',
                            }}
                          >
                            HEADLINE
                          </span>
                        )}
                      </div>
                      <div
                        style={{
                          fontFamily: brand.fSans,
                          fontWeight: 500,
                          fontSize: 12,
                          color: brand.sub,
                          lineHeight: 1.5,
                        }}
                      >
                        {meta.note || flag.description || '—'}
                      </div>
                      {flag.updated_at && (
                        <div
                          style={{
                            fontFamily: brand.fSans,
                            fontWeight: 500,
                            fontSize: 11,
                            color: brand.sub,
                            marginTop: 4,
                            opacity: 0.65,
                          }}
                        >
                          Last changed {new Date(flag.updated_at).toLocaleString()}
                        </div>
                      )}
                    </div>
                  </div>

                  <div style={{ flexShrink: 0 }}>
                    <Tooltip
                      title={
                        flag.enabled
                          ? `Disable "${meta.title}"`
                          : `Enable "${meta.title}"`
                      }
                    >
                      <Switch
                        checked={flag.enabled}
                        onChange={(checked) => toggleFlag(flag.key, checked)}
                        style={
                          isMaster
                            ? { background: flag.enabled ? brand.gold : undefined }
                            : undefined
                        }
                      />
                    </Tooltip>
                  </div>
                </div>
              )
            })}

            {dmApprovalFlag && (
              <div
                data-testid="dm-approval-section"
                style={{ marginTop: integrityFlags.length > 0 ? 24 : 0 }}
              >
                <div
                  style={{
                    fontFamily: brand.fSans,
                    fontWeight: 700,
                    fontSize: 11,
                    letterSpacing: '.08em',
                    textTransform: 'uppercase',
                    color: brand.sub,
                    marginBottom: 10,
                  }}
                >
                  Privacy &amp; consent
                </div>
                <div
                  data-testid={`integrity-row-${dmApprovalFlag.key}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 16,
                    background: 'rgba(106,76,164,.05)',
                    borderRadius: 10,
                    padding: '18px 14px',
                    border: '1px solid rgba(106,76,164,.20)',
                  }}
                >
                  <div
                    style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flex: 1 }}
                  >
                    <div style={{ marginTop: 2, flexShrink: 0 }}>
                      <MessagesSquare size={18} color={brand.jaguar} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontFamily: brand.fSans,
                          fontWeight: 700,
                          fontSize: 13,
                          color: brand.ink,
                          marginBottom: 3,
                        }}
                      >
                        {DM_APPROVAL_META.title}
                        <span
                          style={{
                            marginLeft: 8,
                            fontFamily: brand.fSans,
                            fontWeight: 700,
                            fontSize: 10,
                            color: '#6a4ca4',
                            background: 'rgba(106,76,164,.12)',
                            padding: '2px 7px',
                            borderRadius: 999,
                            verticalAlign: 'middle',
                          }}
                        >
                          CONSENT
                        </span>
                      </div>
                      <div
                        style={{
                          fontFamily: brand.fSans,
                          fontWeight: 500,
                          fontSize: 12,
                          color: brand.sub,
                          lineHeight: 1.5,
                        }}
                      >
                        {dmApprovalFlag.description || DM_APPROVAL_META.note}
                      </div>
                      {dmApprovalFlag.updated_at && (
                        <div
                          style={{
                            fontFamily: brand.fSans,
                            fontWeight: 500,
                            fontSize: 11,
                            color: brand.sub,
                            marginTop: 4,
                            opacity: 0.65,
                          }}
                        >
                          Last changed{' '}
                          {new Date(dmApprovalFlag.updated_at).toLocaleString()}
                        </div>
                      )}
                    </div>
                  </div>

                  <div style={{ flexShrink: 0 }}>
                    <Tooltip
                      title={
                        dmApprovalFlag.enabled
                          ? `Disable "${DM_APPROVAL_META.title}"`
                          : `Enable "${DM_APPROVAL_META.title}"`
                      }
                    >
                      <Switch
                        checked={dmApprovalFlag.enabled}
                        onChange={(checked) =>
                          toggleFlag(dmApprovalFlag.key, checked)
                        }
                      />
                    </Tooltip>
                  </div>
                </div>
              </div>
            )}

            {!isLoading && surfacedFlags.length === 0 && (
              <div
                style={{
                  fontFamily: brand.fSans,
                  fontWeight: 500,
                  fontSize: 13,
                  color: brand.sub,
                  textAlign: 'center',
                  padding: '32px 0',
                }}
              >
                No integrity_* flags found in the feature_flags table.
              </div>
            )}
          </div>
        )}
      </SectionCard>
    </ListPage>
  )
}
